import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layers,
  Star,
  KeyRound,
  Server,
  Database,
  Folder,
  Plus,
  MoreHorizontal,
  ShieldCheck,
  StickyNote,
  Puzzle,
  Search,
  Users,
  X,
} from 'lucide-react';
import OsScroll from './ui/OsScroll';
import {
  useVaultStore,
  selectCategoryCounts,
  selectFolders,
  selectFolderCounts,
  FOLDER_CATEGORY_PREFIX,
} from '../store/vaultStore';
import { registerFolder, mergeFolderSources, loadRegisteredFolders } from '../utils/vaultFolderRegistry';
import { useTeamVaults } from '../hooks/useTeamVaults';
import teamVaultService, { teamVaultErrorMessage } from '../services/teamVaultService';
import { loadTeamVault } from '../store/teamVaultStore';
import {
  buildDestinations,
  canWriteTeamFolder,
  isOfferedDestination,
  teamDestinationValue,
} from '../utils/vaultDestinations';
import VaultSidebarMenu, { newItemSeedFor } from './VaultSidebarMenu';
import VaultFolderDeleteModal from './VaultFolderDeleteModal';
import { deletePersonalFolder, deleteTeamFolder } from '../utils/vaultFolderDelete';
import {
  TEAM_TYPE_CATEGORIES,
  buildTeamSections,
  shouldShowSectionHeadings,
  teamAllCategory,
  teamFolderCategory,
  teamTypeCategory,
} from '../utils/teamVaultSections';
import VaultActionsMenu from './VaultActionsMenu';
import './Vault.css';

/**
 * Vault sidebar: search on top, then the fixed category list (All, Favorites,
 * and one row per item type) with live counts from the envelope list, then the
 * personal folders — and then one section per TEAM whose vault holds something
 * this user may read.
 *
 * Selecting a category / typing a search filters the item list VaultView
 * renders on the left of its split. A team selection filters it to that team's
 * items instead of the personal ones; the two lists are never merged, here or
 * anywhere (see `services/teamVaultService.js`'s header for why that is a
 * security property and not a tidy-up).
 *
 * THE SOLO EXPERIENCE IS UNCHANGED. A user in no team — or in teams whose
 * vaults they hold no grant in — gets zero sections, so no "PERSONAL" heading
 * is drawn and the panel is exactly what it was before teams existed.
 *
 * Vault configuration (passphrase, browser integration, passkeys) lives in
 * Settings → Extensions → Vault (VaultExtensionSettings), not here.
 */
const CATEGORIES = [
  { id: 'all', labelKey: 'vault.catAll', Icon: Layers },
  { id: 'favorites', labelKey: 'vault.catFavorites', Icon: Star },
  { id: 'password', labelKey: 'vault.catPasswords', Icon: KeyRound },
  { id: 'sftp', labelKey: 'vault.catSftp', Icon: Server },
  { id: 'dbConnection', labelKey: 'vault.catDbConnections', Icon: Database },
  { id: 'authHeader', labelKey: 'vault.catAuthHeaders', Icon: ShieldCheck },
  { id: 'note', labelKey: 'vault.catNotes', Icon: StickyNote },
  { id: 'custom', labelKey: 'vault.catCustom', Icon: Puzzle },
];

/** Label + icon per item type, shared by the personal and team lists. */
const TYPE_META = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

function CategoryRow({ id, label, Icon, count, active, onSelect, title, action, onContextMenu }) {
  return (
    <div
      className={`vault-cat-row${active ? ' vault-cat-row--active' : ''}`}
      onClick={() => onSelect(id)}
      onContextMenu={onContextMenu}
      title={title}
    >
      <Icon size={14} />
      <span className="vault-cat-label">{label}</span>
      <span className="vault-cat-count">{count}</span>
      {action}
    </div>
  );
}

/**
 * The small "+" that sits on a heading or a folder row.
 *
 * Rendered even when the user may NOT write there — disabled, with the reason
 * as its tooltip. See `vaultSidebarMenuItems` for why refusing visibly beats
 * hiding: the missing thing is write access, and a user who cannot see the
 * control concludes the feature is broken rather than going to ask an admin.
 *
 * `stopPropagation` because a folder row's own onClick selects the category —
 * clicking "+" must not also change what the panel is showing.
 */
function QuickAddButton({ label, disabled, onActivate, variant = 'add' }) {
  // Headings get "…" and rows get "+". A heading opens a MENU (item or folder),
  // and a "+" that opens a chooser rather than adding something is a small lie;
  // "…" is the conventional mark for "more actions". A folder row really does
  // do one thing, so it keeps the "+".
  const Glyph = variant === 'more' ? MoreHorizontal : Plus;
  return (
    // The wrapper carries the title as well as the button. A DISABLED form
    // control does not receive mouse events in every engine, and the tooltip is
    // the entire reason the refused "+" is shown at all — a refusal with no
    // reason is just a broken button. The wrapper is always hoverable.
    <span className="vault-quick-add-wrap" title={label}>
      <button
        type="button"
        className="vault-folder-add vault-quick-add"
        title={label}
        aria-label={label}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          onActivate(e);
        }}
      >
        <Glyph size={13} />
      </button>
    </span>
  );
}

export default function VaultSidebar() {
  const { t } = useTranslation();
  // Inline, not a modal: creating a folder is one short string, and a dialog for
  // it would be heavier than the thing it collects. Mirrors the "Create new
  // folder…" affordance already in VaultItemModal.
  //
  // WHICH VAULT, not just "am I creating". This was a plain boolean, and the
  // input was rendered in one fixed place — the personal FOLDERS list — so
  // "New folder" on a TEAM heading wrote a personal folder and made it appear
  // under Folders, above the team it was asked for. The two are not variants of
  // one thing: a personal folder is a name in the local `__folders__` registry
  // and never leaves the device, a team folder is a server row with its own
  // grants. `creating` therefore names the scope, and both the input's position
  // and the write below follow from it.
  const [creating, setCreating] = useState(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  // WHY A FAILURE HAS TO BE VISIBLE. The catch below used to swallow everything
  // and only `setSaving(false)`, on the theory that leaving the input open with
  // the text intact was signal enough. It is not: the owner typed a team folder
  // name, pressed Enter, and NOTHING HAPPENED — no folder, no message, no way to
  // tell a permission refusal from an unreachable endpoint. Reported exactly
  // that way. An invisible error is worse than a crash.
  const [createError, setCreateError] = useState('');
  // The folder pending deletion, or null. Holds the TARGET rather than a
  // boolean so the dialog can name the folder and count its contents without
  // re-deriving either — and so closing it cannot leave a stale name behind.
  const [deleting, setDeleting] = useState(null);

  const onCreateFolder = (scope = { kind: 'personal' }) => {
    setNewName('');
    setCreateError('');
    setCreating(scope);
  };

  const commitFolder = async () => {
    const name = newName.trim();
    const scope = creating;
    if (!name || saving || !scope) {
      setCreating(null);
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      if (scope.kind === 'team') {
        await teamVaultService.createFolder(scope.teamId, name);
        // Reload that team so the row appears now. The realtime delta will also
        // arrive, and landing twice is harmless — `loadTeamVault` replaces the
        // entry rather than appending to it.
        await loadTeamVault(scope.teamId);
      } else {
        await registerFolder(name);
        // Refresh so the new (empty) folder appears immediately rather than on
        // the next sync — the registry is a vault item, so `refresh` picks it up.
        await useVaultStore.getState().refresh();
      }
      setCreating(null);
    } catch (err) {
      // Keeps the input open with the text intact — a failed write that closed
      // silently would look like it had worked — and now SAYS WHY. A team folder
      // create can fail for reasons the sidebar cannot predict: the endpoint is
      // missing on an older server, the grant is narrower than the button
      // believed, the name collides. All of those used to render as nothing at
      // all. `teamVaultErrorMessage` unwraps the server's own message.
      setCreateError(
        scope.kind === 'team'
          ? teamVaultErrorMessage(err, t('vault.folderCreateFailed'))
          : t('vault.folderCreateFailed')
      );
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  /** The inline name input, rendered under whichever section asked for it. */
  const folderInput = (
    <div className="vault-folder-new">
      <input
        type="text"
        className="vault-folder-new-input"
        placeholder={t('vault.folderNamePlaceholder')}
        value={newName}
        // NOT `disabled={saving}`. Disabling a FOCUSED input fires `blur` in a
        // real browser, which re-enters `commitFolder`; that second call takes
        // the `saving` early-return and closes the input mid-write, defeating
        // the keep-it-open-on-failure behaviour. jsdom does not fire blur on
        // disable, so no test here can see it. `readOnly` blocks typing without
        // touching focus.
        readOnly={saving}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitFolder();
          if (e.key === 'Escape') setCreating(null);
        }}
        onBlur={commitFolder}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      {createError && <div className="vault-folder-new-error">{createError}</div>}
    </div>
  );
  const activeCategory = useVaultStore((s) => s.activeCategory);

  const search = useVaultStore((s) => s.search);
  const setActiveCategory = useVaultStore((s) => s.setActiveCategory);
  const setSearch = useVaultStore((s) => s.setSearch);
  const items = useVaultStore((s) => s.items);
  // Fresh-object selectors can't be passed to the hook directly (zustand v5
  // getSnapshot caching) — derive from the subscribed slice instead.
  // useMemo over `items`, NOT `useVaultStore(selector)` — the same shape the
  // line below has always used, and for the same reason. These selectors build
  // a fresh array/object on every call, so handing one to the store hook makes
  // useSyncExternalStore see a new snapshot every render and React aborts with
  // "Maximum update depth exceeded". I wrote it the other way first and it made
  // the vault panel unusable.
  // Folders in use, derived from the plaintext index — no decryption needed.
  const inUseFolders = useMemo(() => selectFolders({ items }), [items]);

  // Plus the EMPTY ones, which have no item to derive them from and live in the
  // `__folders__` registry item. Re-read whenever `items` changes, because the
  // registry IS an item — so creating a folder, or another device adding one,
  // both land here through the same refresh.
  const [registered, setRegistered] = useState([]);
  const vaultUnlocked = useVaultStore((s) => !!s.status?.unlocked);
  useEffect(() => {
    // Only while UNLOCKED. Reading the registry needs the DEK, so on a locked
    // vault it would fail with `passphrase_required` — the very token the store
    // turns into a gate prompt. Even swallowed here, issuing a doomed unlock-
    // requiring call during the unlock flow is asking for a second prompt, and
    // it buys nothing: an empty folder simply appears once the vault opens.
    if (!vaultUnlocked) {
      setRegistered([]);
      return undefined;
    }
    let alive = true;
    loadRegisteredFolders().then((list) => {
      if (alive) setRegistered(list);
    });
    return () => {
      alive = false;
    };
  }, [items, vaultUnlocked]);

  // The union — and `inUseFolders` first, so a folder that actually holds
  // something keeps the casing its items carry. The registry can only ADD
  // empty folders; it can never remove or rename a real one.
  const folders = useMemo(
    () => mergeFolderSources(inUseFolders, registered),
    [inUseFolders, registered]
  );
  const folderCounts = useMemo(() => selectFolderCounts({ items }), [items]);
  const counts = useMemo(() => selectCategoryCounts({ items }), [items]);

  // Team sections. `useTeamVaults` returns the RAW store slices (both stable
  // references); `buildTeamSections` builds a fresh array, so it goes through
  // useMemo for exactly the reason above.
  const { teams, byTeam } = useTeamVaults();
  const teamSections = useMemo(() => buildTeamSections({ teams, byTeam }), [teams, byTeam]);
  const showHeadings = shouldShowSectionHeadings(teamSections);

  // ── quick create ──────────────────────────────────────────────────────────
  //
  // WHICH TEAMS MAY BE WRITTEN TO. Deliberately `buildDestinations` — the same
  // function that decides what the New Item modal's destination picker offers —
  // rather than a second reading of `can_write` here. Two notions of "writable"
  // is how a sidebar ends up offering a save the modal would refuse (or the
  // server would 403). Fresh array, so useMemo, per the store-hook rule above.
  const destinations = useMemo(() => buildDestinations({ teams, byTeam }), [teams, byTeam]);

  // `{ x, y, target }` — one piece of state for both routes, because the "+"
  // and the right-click open the same menu on the same target.
  const [menu, setMenu] = useState(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const openMenuAt = (e, target) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget?.getBoundingClientRect?.();
    // A right-click opens at the cursor; a "+" opens under the button (a
    // PointerEvent from a click carries clientX/Y too, so the type of the
    // event is not a reliable discriminator — the caller says which).
    const at =
      target.anchor === 'button' && rect
        ? { x: rect.left, y: rect.bottom + 4 }
        : { x: e.clientX, y: e.clientY };
    setMenu({ ...at, target });
  };

  /**
   * Open the New Item modal, pre-filed.
   *
   * VaultView owns the modal (it holds the item list the folder picker needs),
   * and the sidebar is a sibling — so this goes through the SAME
   * `vault-new-item` CustomEvent the titlebar "+" already dispatches, now with
   * a `detail`. The titlebar keeps dispatching none, which is exactly the
   * "blank dialog" behaviour it had before.
   */
  const openNewItem = (target) => {
    const seed = newItemSeedFor(target);
    if (!seed) return; // refused: not writable. Never reachable from an enabled control.
    window.dispatchEvent(new CustomEvent('vault-new-item', { detail: seed }));
  };

  const runMenuAction = (id, target) => {
    if (id === 'new-folder') {
      // Scoped to the section the menu was opened on. `new-folder` is only ever
      // offered on the two HEADINGS (personal, a team) — a folder row does not
      // offer it — so a `team` target means a team folder and anything else
      // means a personal one.
      onCreateFolder(
        target?.kind === 'team' ? { kind: 'team', teamId: target.teamId } : { kind: 'personal' }
      );
      return;
    }
    if (id === 'new-item') {
      openNewItem(target);
      return;
    }
    if (id === 'delete-folder') {
      // The menu already refused this for a target without the grant; opening
      // the dialog anyway would be a second control promising what the first
      // declined.
      if (target?.kind === 'teamFolder' && !target.canManageFolders) return;
      setDeleting(target);
    }
  };

  /** Run the delete the dialog confirmed. Throws so the dialog can show why. */
  const performFolderDelete = async ({ deleteItems }) => {
    const target = deleting;
    if (!target) return;

    // If the panel is currently FILTERED TO the folder being deleted, move it
    // off first. `activeCategory` would otherwise point at a folder that no
    // longer exists and the list would render permanently empty, with no row
    // left to click to escape it.
    const deletedCategory =
      target.kind === 'teamFolder'
        ? teamFolderCategory(target.teamId, target.folderId)
        : `${FOLDER_CATEGORY_PREFIX}${target.folder}`;
    const wasViewingIt = activeCategory === deletedCategory;

    if (target.kind === 'teamFolder') {
      const entry = byTeam?.[String(target.teamId)];
      await deleteTeamFolder({
        teamId: target.teamId,
        folderId: target.folderId,
        items: entry?.items || [],
        deleteItems,
      });
      await loadTeamVault(target.teamId);
      // AFTER the delete, not before: a failed delete used to navigate the user
      // away from a folder that still exists.
      if (wasViewingIt) setActiveCategory('all');
      return;
    }

    await deletePersonalFolder({ folder: target.folder, items, deleteItems });
    await useVaultStore.getState().refresh();
    setRegistered(await loadRegisteredFolders());
    if (wasViewingIt) setActiveCategory('all');
  };

  // The PERSONAL heading's target. Its "+" opens a MENU rather than acting,
  // because personal offers two things (item, folder) and a "+" that silently
  // picked one of them would make the other undiscoverable.
  const personalTarget = { kind: 'personal', anchor: 'button' };

  return (
    <div className="vault-sidebar">
      {/* Fixed filter header — mirrors ExtensionSidebarSections/Collections */}
      <div className="flex-shrink-0 px-2 pt-2 border-b border-gray-200">
        {/* The vault's actions sit here since they left the title bar (VaultActionsMenu). */}
        <div className="vault-sidebar-head">
        <div className="sidebar-panel-search-row">
          <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
          <input
            type="text"
            placeholder={t('vault.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
            autoComplete="off"
          />
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                aria-label={t('sidebar.clearSearch', 'Clear filter')}
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
        <VaultActionsMenu />
        </div>
      </div>

      <OsScroll style={{ flex: 1, minHeight: 0 }}>
        {/* Drawn only when a team section follows it: on a vault with one
            section a heading labels the only thing there. */}
        {/* The "+" lives ON the heading, so it exists exactly when the heading
            does — i.e. only once a team section is drawn beside it. A solo
            user's sidebar is untouched: their two routes to the same two
            actions are the titlebar "+" and the FOLDERS "+", both unchanged. */}
        {showHeadings && (
          <div
            className="vault-section-heading vault-section-heading--first vault-section-heading--with-action"
            onContextMenu={(e) => openMenuAt(e, { kind: 'personal' })}
          >
            <span>{t('vault.sectionPersonal', 'Personal')}</span>
            <QuickAddButton
              variant="more"
              label={t('vault.newInPersonal')}
              onActivate={(e) => openMenuAt(e, personalTarget)}
            />
          </div>
        )}
        <div className="vault-cat-list">
          {CATEGORIES.map(({ id, labelKey, Icon }) => (
            <CategoryRow
              key={id}
              id={id}
              label={t(labelKey)}
              Icon={Icon}
              count={counts[id] || 0}
              active={activeCategory === id}
              onSelect={setActiveCategory}
            />
          ))}
        </div>

        {/* Folders. Rendered only when at least one exists — a permanently empty
            "Folders" heading on a vault nobody has organised is noise, and this
            list is derived from items rather than a registry, so "none yet" and
            "none ever" are the same state. */}
        {/* Always rendered, even with no folders: the heading is where the "+"
            lives, and a create affordance that only appears once you already
            have one is a chicken-and-egg. */}
        {true && (
          <div className="vault-cat-list vault-folder-list">
            <div className="vault-folder-heading vault-folder-heading--with-action">
              <span>{t('vault.folders')}</span>
              {/* The SAME control as the Personal/team headings, not a bespoke
                  button. It had its own markup and its own class, so it sat at a
                  different size and a different x than the "…" one row above it
                  — two create affordances on adjacent headings that did not line
                  up. Sharing the component makes them the same by construction,
                  hover-reveal included. */}
              <QuickAddButton
                label={t('vault.newFolder')}
                onActivate={() => onCreateFolder({ kind: 'personal' })}
              />
            </div>
            {creating?.kind === 'personal' && folderInput}
            {folders.length === 0 && creating?.kind !== 'personal' && (
              <div className="vault-folder-empty">{t('vault.noFoldersYet')}</div>
            )}
            {folders.map((name) => {
              const id = `folder:${name}`;
              // The "+" here does NOT open a blank dialog — that is what the
              // titlebar "+" is for. It opens the New Item modal already filed
              // into THIS folder, which is the entire reason the affordance is
              // on the row rather than only on the heading. The user can still
              // change it in the modal.
              const target = {
                kind: 'personalFolder',
                folder: name,
                folderName: name,
                itemCount: folderCounts[name] || 0,
              };
              return (
                <CategoryRow
                  key={id}
                  id={id}
                  label={name}
                  Icon={Folder}
                  count={folderCounts[name] || 0}
                  active={activeCategory === id}
                  onSelect={setActiveCategory}
                  title={name}
                  onContextMenu={(e) => openMenuAt(e, target)}
                />
              );
            })}
          </div>
        )}

        {/* One section per team holding something this user may read. A team
            with no grant — or with grants on empty folders — produces nothing
            at all rather than an empty heading. */}
        {teamSections.map((section) => {
          // Writable ANYWHERE in this team — the same question the destination
          // picker asks, asked through the same function. False keeps the "+"
          // on screen and disables it with the reason.
          const teamWritable = isOfferedDestination(
            destinations,
            teamDestinationValue(section.teamId)
          );
          // Folder MANAGEMENT is a stricter grant than writing an item — see
          // the FOLDERS heading below.
          const canManageFolders = !!byTeam?.[String(section.teamId)]?.canManageFolders;
          const teamTarget = {
            kind: 'team',
            teamId: section.teamId,
            canWrite: teamWritable,
            canManageFolders,
          };
          return (
          <div key={section.teamId} className="vault-team-section">
            <div
              className="vault-section-heading vault-section-heading--team vault-section-heading--with-action"
              title={section.teamName}
              onContextMenu={(e) => openMenuAt(e, teamTarget)}
            >
              <Users size={11} aria-hidden />
              <span className="vault-team-name">{section.teamName}</span>
              <QuickAddButton
                variant="more"
                label={
                  teamWritable ? t('vault.newInTeam') : t('vault.noTeamWriteAccess')
                }
                disabled={!teamWritable}
                onActivate={(e) => openMenuAt(e, teamTarget)}
              />
            </div>
            {/* An empty team says WHY, rather than showing bare category rows
                that all read 0. The two reasons need different actions from the
                user — wait, versus go ask an admin — so they get different
                text. */}
            {section.emptyReason ? (
              <div className="vault-team-empty">
                {section.emptyReason === 'no-access'
                  ? t('vault.teamVaultNoAccess')
                  : t('vault.teamVaultEmpty')}
              </div>
            ) : (
            <>
            <div className="vault-cat-list">
              <CategoryRow
                id={teamAllCategory(section.teamId)}
                label={t('vault.catAll')}
                Icon={Layers}
                count={section.counts.all || 0}
                active={activeCategory === teamAllCategory(section.teamId)}
                onSelect={setActiveCategory}
              />
              {/* The same type rows as the personal list, in the same order.
                  No Favorites row: `favorite` is a personal-vault column and a
                  team row that could only ever read 0 promises a feature that
                  does not exist. */}
              {TEAM_TYPE_CATEGORIES.map((type) => {
                const meta = TYPE_META[type];
                if (!meta) return null;
                const id = teamTypeCategory(section.teamId, type);
                return (
                  <CategoryRow
                    key={id}
                    id={id}
                    label={t(meta.labelKey)}
                    Icon={meta.Icon}
                    count={section.counts[type] || 0}
                    active={activeCategory === id}
                    onSelect={setActiveCategory}
                  />
                );
              })}
            </div>

            {/* Also drawn while a folder is being NAMED for this team, even if
                the team has no folders yet — otherwise "New folder" on the
                heading opens an input with nowhere to appear, which is how it
                ended up rendering under the personal Folders list instead. */}
            {(section.folders.length > 0 ||
              section.rootCount > 0 ||
              (creating?.kind === 'team' && creating.teamId === section.teamId)) && (
              <div className="vault-cat-list vault-folder-list">
                <div className="vault-folder-heading vault-folder-heading--with-action">
                  <span>{t('vault.folders')}</span>
                  {/* NOT `teamWritable`. Creating a folder needs FULL write
                      scope on the server (`require_full_write`), which a member
                      holding a grant on one folder does not have — they would
                      have got an enabled button and a 403. The server reports
                      the answer directly; see teamVaultService.listFolders. */}
                  <QuickAddButton
                    label={
                      canManageFolders
                        ? t('vault.newFolder')
                        : t('vault.noTeamFolderManageAccess')
                    }
                    disabled={!canManageFolders}
                    onActivate={() =>
                      onCreateFolder({ kind: 'team', teamId: section.teamId })
                    }
                  />
                </div>
                {creating?.kind === 'team' && creating.teamId === section.teamId && folderInput}
                {/* NO "Team vault (no folder)" ROW. Removed 06.08.2026 at the
                    owner's request, and it was misleading twice over: it sat
                    under a FOLDERS heading while not being a folder, and it was
                    gated on `rootCount > 0`, so filing your last root item into
                    a real folder made it VANISH — which reads as a folder
                    disappearing rather than as an empty bucket hiding.
                    Everything it offered survives elsewhere: "All items" shows
                    the unfiled ones alongside the rest, and the team heading's
                    "…" still creates at the root. */}
                {section.folders.map((folder) => {
                  const id = teamFolderCategory(section.teamId, folder.id);
                  // PER FOLDER, not per team: a grant on "Production" says
                  // nothing about "Staging", and the "+" sits on one of them.
                  const target = {
                    kind: 'teamFolder',
                    teamId: section.teamId,
                    folderId: folder.id,
                    folderName: folder.name,
                    itemCount: folder.count,
                    canWrite: canWriteTeamFolder(byTeam, section.teamId, folder.id),
                    // Deleting a folder is `require_full_write`, not the
                    // per-folder write above — see vaultSidebarMenuItems.
                    canManageFolders,
                  };
                  return (
                    <CategoryRow
                      key={id}
                      id={id}
                      label={folder.name}
                      Icon={Folder}
                      count={folder.count}
                      active={activeCategory === id}
                      onSelect={setActiveCategory}
                      title={folder.name}
                      onContextMenu={(e) => openMenuAt(e, target)}
                    />
                  );
                })}
              </div>
            )}
            </>
            )}
          </div>
          );
        })}
      </OsScroll>

      {/* One menu for both routes. Outside the scroller so the portal's owner
          is not unmounted mid-scroll. */}
      {/* Outside `menu &&` deliberately: `runMenuAction` closes the menu before
          it runs, so a dialog nested inside that branch would unmount the
          instant it was asked for. */}
      {deleting && (
        <VaultFolderDeleteModal
          target={deleting}
          itemCount={deleting.itemCount || 0}
          onClose={() => setDeleting(null)}
          onConfirm={performFolderDelete}
        />
      )}
      {menu && (
        <VaultSidebarMenu
          x={menu.x}
          y={menu.y}
          target={menu.target}
          t={t}
          onClose={closeMenu}
          onSelect={(id) => runMenuAction(id, menu.target)}
        />
      )}
    </div>
  );
}
